import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { C, Sum } from 'shared/helpers';

import Styles from './Editor.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

import NumberFormat from 'react-number-format';
import { useIntl } from 'react-intl';

function StyleCheck(sum) {
  return sum === 100
    ? Styles.Checked
    : sum < 100
    ? Styles.Remaining
    : Styles.Exceded;
}

/**
 *
 * @param {Object} param0
 * @param {string[]} param0.baseCategories
 */
export default function Editor({
  id,
  etec,
  open,
  close,
  submit,
  produtividade = 0,
  baseCategories,
}) {
  const {
    Editor,
    Container,
    Form,
    Open,
    Row,
    Block,
    Leftist,
    Rightist,
    Final,
    Footer,
    Save,
    Close,
  } = Styles;

  const { formatNumber } = useIntl();
  const impactos = useSelector((state) => state.impactos.data);
  const block = useRef(false);
  function _submit(form) {
    if (block.current) return;
    block.current = true;
    submit(form);
  }
  useEffect(() => {
    if (open) block.current = false;
  }, [open]);

  const descarte = baseCategories.slice(-1)[0];
  const [form, setForm] = useState({
    pem_id: Number(id),
    etec_id: etec,
    fase_id: 9,
    classificacao: baseCategories.map((c) =>
      c === descarte ? c : `Categoria ${c}`,
    ),
    porcentagem: {
      svb: [...baseCategories.map(() => 0)],
      convencional: [...baseCategories.map(() => 0)],
    },
    preco: {
      svb: [...baseCategories.slice(0, -1).map(() => 0)],
      convencional: [...baseCategories.slice(0, -1).map(() => 0)],
    },
    produtividade: 0,
  });

  const catInputChange = ({ target: { value } }, index) => {
    setForm((f) => ({
      ...f,
      classificacao: [
        ...(f.classificacao || []).map((c, i) => (i === index ? value : c)),
      ],
      preco: value
        ? { ...f.preco }
        : {
            ...f.preco,
            svb: {
              ...f.preco.svb,
              [index]: 0,
              [index + 1]: value ? f.preco.svb[index + 1] : 0,
            },
            convencional: {
              ...f.preco.convencional,
              [index]: 0,
              [index + 1]: value ? f.preco.convencional[index + 1] : 0,
            },
          },
      porcentagem: value
        ? { ...f.porcentagem }
        : {
            ...f.porcentagem,
            svb: {
              ...f.porcentagem.svb,
              [index]: 0,
              [index + 1]: value ? f.porcentagem.svb[index + 1] : 0,
            },
            convencional: {
              ...f.porcentagem.convencional,
              [index]: 0,
              [index + 1]: value ? f.porcentagem.convencional[index + 1] : 0,
            },
          },
    }));
  };

  useEffect(() => {
    if (
      impactos &&
      impactos instanceof Object &&
      impactos.classificacao &&
      impactos.preco &&
      impactos.porcentagem
    ) {
      const descarte = baseCategories.slice(-1)[0];
      setForm({
        ...impactos,
        produtividade: Number(impactos.produtividade),
        classificacao: baseCategories.reduce(
          (arrCat, cat, i) => [
            ...arrCat,
            impactos.classificacao[i] ||
              (cat === descarte ? cat : `Categoria ${cat}`),
          ],
          [],
        ),
        preco: {
          svb: baseCategories.reduce(
            (arrCat, _, i) => [...arrCat, Number(impactos.preco.svb[i]) || 0],
            [],
          ),
          convencional: baseCategories.reduce(
            (arrCat, _, i) => [...arrCat, Number(impactos.preco.convencional[i]) || 0],
            [],
          ),
        },
        porcentagem: {
          svb: baseCategories.reduce(
            (arrCat, _, i) => [...arrCat, Number(impactos.porcentagem.svb[i]) || 0],
            [],
          ),
          convencional: baseCategories.reduce(
            (arrCat, _, i) => [
              ...arrCat,
              Number(impactos.porcentagem.convencional[i]) || 0,
            ],
            [],
          ),
        },
      });
    }
  }, [impactos, open, baseCategories]);

  const InputChange = ({ floatValue = null }, index, svb, key) =>
    setForm((f) => ({
      ...f,
      [key]: {
        ...f[key],
        svb: svb
          ? {
              ...f[key].svb,
              [index]: !index || f.classificacao[index - 1] ? floatValue : 0,
            }
          : { ...f[key].svb },
        convencional: !svb
          ? {
              ...f[key].convencional,
              [index]: !index || f.classificacao[index - 1] ? floatValue : 0,
            }
          : { ...f[key].convencional },
      },
    }));

  const NumberInput = ({ i, p, s, d, v, f, dec = '2' } = {}) => (
    <NumberFormat
      data-testid={i}
      onValueChange={f}
      disabled={d || undefined}
      value={v}
      prefix={p || ''}
      suffix={s || ''}
      decimalScale={dec}
      fixedDecimalScale={dec}
      thousandSeparator='.'
      decimalSeparator=','
    />
  );

  const percentualSQM = Sum(
    Object.values(form.porcentagem.svb).map((v) => v || 0),
    (r) => Number(r.toFixed(2)),
  );
  const percentualConv = Sum(
    Object.values(form.porcentagem.convencional).map((v) => v || 0),
    (r) => Number(r.toFixed(2)),
  );

  return (
    <div className={C(Editor, [open, Open])}>
      <main className={C([open, Open])}>
        <header onClick={close}>
          Impactos de Qualidade
          <div id='impactos_close_btn'>
            <FontAwesomeIcon icon={faTimes} />
          </div>
        </header>
        <div className={Container}>
          <div className={Form}>
            <header>
              <div>Item</div>
              <div>Recomendação SQM VITAS Brasil</div>
              <div>Recomendação Convencional</div>
            </header>
            <div className={Row}>
              <div className={C(Block, Leftist)}>Produtividade em /ha</div>
              <div className={C(Block)}>
                {NumberInput({
                  s: ' /ha',
                  d: false,
                  v: form.produtividade,
                  f: (fv) => setForm({ ...form, produtividade: fv.floatValue }),
                })}
              </div>
              <div className={C(Block, Rightist)}>
                <p>
                  {formatNumber(produtividade, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
            </div>
            {baseCategories.map((cat, index) => (
              <div className={Row}>
                <div className={Block}>
                  <input
                    data-testid={`classificacao_nome_${index.toString()}`}
                    onChange={(e) => catInputChange(e, index)}
                    type='text'
                    value={form.classificacao[index] || ''}
                    placeholder={`Categoria ${cat}`}
                  />
                </div>
                <div className={Block}>
                  {NumberInput({
                    i: `classificacao_percent_conv_${index.toString()}`,
                    s: '%',
                    d: !form.classificacao[index],
                    v: form.porcentagem.svb[index],
                    f: (fv) => InputChange(fv, index, true, 'porcentagem'),
                  })}
                </div>
                <div className={Block}>
                  {NumberInput({
                    i: `classificacao_percent_svb_${index.toString()}`,
                    s: '%',
                    d: !form.classificacao[index],
                    v: form.porcentagem.convencional[index],
                    f: (fv) => InputChange(fv, index, false, 'porcentagem'),
                  })}
                </div>
              </div>
            ))}
            {baseCategories.slice(0, -1).map((cat, index) => (
              <div className={Row}>
                <div className={C(Block, Leftist)}>
                  Preço do Produto{' '}
                  {form.classificacao[index] || `Categoria ${cat}`}
                </div>
                <div className={C(Block)}>
                  {NumberInput({
                    p: 'R$ ',
                    d: !form.classificacao[index],
                    v: Number(form.preco.svb[index]),
                    f: (fv) => InputChange(fv, index, true, 'preco'),
                  })}
                </div>
                <div className={Block}>
                  {NumberInput({
                    p: 'R$ ',
                    d: !form.classificacao[index],
                    v: Number(form.preco.convencional[index]),
                    f: (fv) => InputChange(fv, index, false, 'preco'),
                  })}
                </div>
              </div>
            ))}
            <div className={Row}>
              <div className={C(Block, Final)} />
              <div className={C(Block, Final)} />
              <div className={C(Block, Final)} />
            </div>
          </div>
        </div>
        <div className={Styles.Container}>
          <div
            className={C(Row, Styles.Check, [
              percentualConv + percentualSQM === 200,
              Styles.Valid,
            ])}
          >
            <div className={C(Block)} />
            <div className={C(Block, StyleCheck(percentualSQM))}>
              {percentualSQM === 100
                ? ''
                : percentualSQM < 100
                ? `( ! ) Preenchido: ${formatNumber(percentualSQM)}% de 100%`
                : 'Ultrapassou 100%'}
            </div>
            <div className={C(Block, StyleCheck(percentualConv))}>
              {percentualConv === 100
                ? ''
                : percentualConv < 100
                ? `( ! ) Preenchido: ${formatNumber(percentualConv)}% de 100%`
                : 'Ultrapassou 100%'}
            </div>
          </div>
          <div className={C(Row)}>
            <div className={C(Block)}>% de classficação dos produtos</div>
            <div className={C(Block, StyleCheck(percentualSQM))}>
              {formatNumber(percentualSQM)}%
            </div>
            <div className={C(Block, StyleCheck(percentualConv))}>
              {formatNumber(percentualConv)}%
            </div>
          </div>
        </div>
        <div className={Footer}>
          <button
            data-testid='impactos_qualidade_button_submit'
            onClick={() => _submit(form)}
            className={Save}
            id='btn_salvar_respostas'
            disabled={percentualConv !== 100 || percentualSQM !== 100}
          >
            Salvar
          </button>
          <button onClick={close} className={Close}>
            Fechar
          </button>
        </div>
      </main>
    </div>
  );
}

Editor.defaultProps = {
  open: false,
};

Editor.propTypes = {
  open: PropTypes.bool,
  close: PropTypes.func.isRequired,
  id: PropTypes.any.isRequired,
  etec: PropTypes.any.isRequired,
  submit: PropTypes.func.isRequired,
};
